// maths.portll.net — 4-digit numeric PIN pad.
//
// A self-contained, iPad-safe keypad. It owns the digit-entry state; the CALLER owns
// what "verify" means — pass an async `verify(pin)` that resolves to the callable
// result shape from functions/index.js:
//   { ok:true }                                   → success (onSuccess is called)
//   { ok:false, attemptsLeft:n }                  → wrong PIN, n tries left
//   { ok:false, locked:true, lockedUntil:<ms> }   → temporarily locked out
// It surfaces attemptsLeft / locked / lockedUntil straight from that result and runs a
// live countdown while locked. Every key is a real <button> (keyboard-operable, ≥44px);
// a physical number key / backspace also works while the pad is focused.

import { useCallback, useEffect, useRef, useState } from "react";
import { injectStyles, Spinner } from "./ui.jsx";

const PIN_LENGTH = 4;

// Layout: 1–9, then [clear] 0 [backspace].
const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "clear", "0", "back"];

function friendlyError(e) {
  const msg = (e && e.message) || "";
  if (/network|unavailable|internal/i.test(msg)) return "Couldn't reach the server — check your connection and try again.";
  return msg || "Something went wrong. Please try again.";
}

export default function PinPad({
  title,
  subtitle,
  verify,
  onSuccess,
  disabled = false,
}) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // { tone: 'warn'|'danger', text }
  const [lockedUntil, setLockedUntil] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const submittingRef = useRef(false);

  const isLocked = lockedUntil > now;
  const blocked = disabled || busy || isLocked;

  // Live countdown while locked, so the pad re-enables itself on its own.
  useEffect(() => {
    if (!isLocked) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [isLocked]);

  const submit = useCallback(
    async (value) => {
      if (submittingRef.current) return;
      submittingRef.current = true;
      setBusy(true);
      setMsg(null);
      try {
        const res = await verify(value);
        if (res && res.ok) {
          onSuccess?.(res);
          return; // leave the pad as-is; the caller navigates / unmounts
        }
        if (res && res.locked) {
          setLockedUntil(Number(res.lockedUntil) || Date.now() + 5 * 60 * 1000);
          setMsg({ tone: "danger", text: "Too many tries. Take a short break, then try again." });
        } else if (res && typeof res.attemptsLeft === "number") {
          const n = res.attemptsLeft;
          setMsg({ tone: "warn", text: `That PIN didn't match — ${n} ${n === 1 ? "try" : "tries"} left.` });
        } else {
          setMsg({ tone: "danger", text: "That PIN didn't match." });
        }
        setPin("");
      } catch (e) {
        setMsg({ tone: "danger", text: friendlyError(e) });
        setPin("");
      } finally {
        setBusy(false);
        submittingRef.current = false;
      }
    },
    [verify, onSuccess]
  );

  // Auto-submit the moment the 4th digit lands.
  useEffect(() => {
    if (pin.length === PIN_LENGTH && !submittingRef.current) submit(pin);
  }, [pin, submit]);

  const pressDigit = useCallback(
    (d) => {
      if (blocked) return;
      setMsg(null);
      setPin((p) => (p.length >= PIN_LENGTH ? p : p + d));
    },
    [blocked]
  );
  const backspace = useCallback(() => {
    if (blocked) return;
    setPin((p) => p.slice(0, -1));
  }, [blocked]);
  const clearAll = useCallback(() => {
    if (blocked) return;
    setPin("");
    setMsg(null);
  }, [blocked]);

  function onKeyDown(e) {
    if (e.key >= "0" && e.key <= "9") {
      pressDigit(e.key);
      e.preventDefault();
    } else if (e.key === "Backspace") {
      backspace();
      e.preventDefault();
    } else if (e.key === "Escape") {
      clearAll();
    }
  }

  const remaining = Math.max(0, lockedUntil - now);
  const mm = Math.floor(remaining / 60000);
  const ss = Math.floor((remaining % 60000) / 1000);

  return (
    <div className="pinpad" role="group" aria-label={title || "Enter PIN"} tabIndex={0} onKeyDown={onKeyDown}>
      {title && <h2 className="pinpad__title">{title}</h2>}
      {subtitle && <p className="pinpad__sub">{subtitle}</p>}

      <div className="pinpad__dots" aria-hidden="true">
        {Array.from({ length: PIN_LENGTH }).map((_, i) => (
          <span key={i} className={"pinpad__dot" + (i < pin.length ? " pinpad__dot--on" : "")} />
        ))}
      </div>

      <div className="pinpad__status" role="status" aria-live="polite">
        {busy ? (
          <span className="pinpad__busy"><Spinner size={18} label="Checking" /> Checking…</span>
        ) : isLocked ? (
          <span className="pinpad__msg pinpad__msg--danger">
            Locked — try again in {mm}:{String(ss).padStart(2, "0")}
          </span>
        ) : msg ? (
          <span className={"pinpad__msg pinpad__msg--" + msg.tone}>{msg.text}</span>
        ) : (
          <span className="pinpad__hint">Enter the 4-digit PIN</span>
        )}
      </div>

      <div className="pinpad__grid">
        {KEYS.map((k) => {
          if (k === "clear")
            return (
              <button
                key="clear"
                type="button"
                className="pinpad__key pinpad__key--util"
                onClick={clearAll}
                disabled={blocked || pin.length === 0}
                aria-label="Clear all digits"
              >
                Clear
              </button>
            );
          if (k === "back")
            return (
              <button
                key="back"
                type="button"
                className="pinpad__key pinpad__key--util"
                onClick={backspace}
                disabled={blocked || pin.length === 0}
                aria-label="Delete last digit"
              >
                <span aria-hidden="true">⌫</span>
              </button>
            );
          return (
            <button
              key={k}
              type="button"
              className="pinpad__key"
              onClick={() => pressDigit(k)}
              disabled={blocked}
              aria-label={`Digit ${k}`}
            >
              {k}
            </button>
          );
        })}
      </div>
    </div>
  );
}

injectStyles(
  "maths-pinpad-styles",
  `
.pinpad{max-width:20rem;margin:0 auto;text-align:center;outline:none;}
.pinpad:focus-visible{outline:2px solid var(--sky);outline-offset:6px;border-radius:18px;}
.pinpad__title{margin:0 0 .25rem;font-size:1.15rem;}
.pinpad__sub{margin:0 0 .5rem;color:var(--ink-soft);font-size:.92rem;}
.pinpad__dots{display:flex;gap:.85rem;justify-content:center;margin:1rem 0 .35rem;}
.pinpad__dot{width:15px;height:15px;border-radius:50%;border:2px solid var(--brand);background:transparent;transition:background .1s ease;}
.pinpad__dot--on{background:var(--brand);}
.pinpad__status{min-height:1.5rem;margin:.15rem 0 .75rem;font-size:.9rem;}
.pinpad__busy{display:inline-flex;align-items:center;gap:.45rem;color:var(--ink-soft);}
.pinpad__hint{color:var(--ink-soft);}
.pinpad__msg--warn{color:#7a4a10;font-weight:600;}
.pinpad__msg--danger{color:var(--danger);font-weight:600;}
.pinpad__grid{display:grid;grid-template-columns:repeat(3,1fr);gap:.6rem;}
.pinpad__key{min-height:60px;font-size:1.4rem;font-weight:700;border-radius:14px;
  background:var(--card);color:var(--brand-ink);border:1px solid var(--line);cursor:pointer;
  transition:transform .04s ease;}
.pinpad__key:active{transform:translateY(1px);}
.pinpad__key:focus-visible{outline:2px solid var(--sky);outline-offset:2px;}
.pinpad__key:disabled{opacity:.45;cursor:default;transform:none;}
.pinpad__key--util{font-size:1.05rem;font-weight:600;color:var(--ink-soft);background:transparent;}
`
);
